<div class="min-h-screen p-6 md:p-10 space-y-6 bg-primary-50">

  <!-- Header -->
  <header class="flex items-center gap-4">
    <button type="button"
            (click)="back()"
            class="inline-flex items-center justify-center w-20 h-20 rounded-xl border border-primary-200 hover:bg-gray-50 focus:outline-none focus:ring"
            aria-label="Back to Home">
      <i class="pi pi-arrow-left !text-5xl"></i>
    </button>
    <div class="flex-1">
      <h1 class="text-2xl md:text-3xl font-bold tracking-tight">{{ space ? space.name : 'Space Admin' }}</h1>
      <p class="text-sm text-gray-500" *ngIf="space">Shared Space administration</p>
    </div>
  </header>

  <!-- Loading spinner -->
  <div *ngIf="loading" class="flex justify-center items-center py-20">
    <i class="pi pi-spin pi-spinner text-4xl text-primary-400"></i>
  </div>

  <!-- Load error -->
  <div *ngIf="!loading && loadError" class="flex flex-col items-center gap-3 py-20 text-gray-400">
    <i class="pi pi-exclamation-triangle text-5xl"></i>
    <p>{{ loadError }}</p>
    <p-button label="Back to Home" severity="secondary" (onClick)="back()" />
  </div>

  <!-- Not authorized -->
  <div *ngIf="!loading && !loadError && notAuthorized" class="flex flex-col items-center gap-3 py-20 text-gray-400">
    <i class="pi pi-lock text-5xl"></i>
    <p>You need to be an Owner or Admin of this Space to administer it.</p>
    <p-button label="Back to Home" severity="secondary" (onClick)="back()" />
  </div>

  <ng-container *ngIf="!loading && !loadError && !notAuthorized && space">

    <!-- Each section collapses from its header (title always shown); the open/closed state is remembered. -->
    <ng-template #sectionHeader let-id let-title="title">
      <button
        type="button"
        class="flex w-full cursor-pointer items-center gap-3 border-0 bg-transparent px-4 py-3 text-left md:px-6 md:py-4 hover:bg-gray-50 dark:hover:bg-surface-800 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary"
        [attr.aria-expanded]="!isCollapsed(id)"
        [attr.aria-controls]="'space-admin-section-' + id"
        [attr.data-testid]="'space-admin-toggle-' + id"
        (click)="toggleSection(id)">
        <h2 class="m-0 flex-1 text-lg font-semibold text-gray-800 dark:text-surface-100" [id]="'space-admin-heading-' + id">{{ title }}</h2>
        <span
          class="inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-600 dark:text-surface-300"
          [title]="isCollapsed(id) ? 'Expand' : 'Collapse'"
          aria-hidden="true">
          <span
            class="material-symbols-outlined transition-transform duration-200"
            [style.transform]="isCollapsed(id) ? 'rotate(-90deg)' : 'rotate(0deg)'">expand_more</span>
        </span>
      </button>
    </ng-template>

    <!-- Space details -->
    <section class="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-surface-700 dark:bg-surface-900" aria-labelledby="space-admin-heading-details">
      <ng-container *ngTemplateOutlet="sectionHeader; context: { $implicit: 'details', title: 'Space details' }"></ng-container>
      <div id="space-admin-section-details" class="space-y-4 px-4 pb-4 md:px-6 md:pb-6" [hidden]="isCollapsed('details')">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1" for="space-admin-name">Name</label>
          <input pInputText
                 id="space-admin-name"
                 type="text"
                 [(ngModel)]="editName"
                 maxlength="255"
                 class="w-full"
                 autocomplete="off" />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1" for="space-admin-description">Description</label>
          <input pInputText
                 id="space-admin-description"
                 type="text"
                 [(ngModel)]="editDescription"
                 maxlength="250"
                 class="w-full"
                 autocomplete="off" />
        </div>

        <p *ngIf="saveSpaceError" class="text-sm text-red-600">{{ saveSpaceError }}</p>

        <div class="flex justify-end">
          <p-button
            label="Save"
            [loading]="savingSpace"
            [disabled]="!spaceInfoDirty || savingSpace"
            (onClick)="saveSpaceInfo()" />
        </div>
      </div>
    </section>

    <!-- Space tags: this Space's own tags only (shared with every member). Same editor as the user's
         private tags (Manage → My Private Tags), via the shared <app-tag-manager>. -->
    <section class="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-surface-700 dark:bg-surface-900" aria-labelledby="space-admin-heading-tags">
      <ng-container *ngTemplateOutlet="sectionHeader; context: { $implicit: 'tags', title: 'Space tags' }"></ng-container>
      <div id="space-admin-section-tags" class="px-4 pb-4 md:px-6 md:pb-6" [hidden]="isCollapsed('tags')">
        <p class="mb-3 mt-0 text-sm text-gray-500">
          Tags shared with every member of {{ space.name }}, for this Space's notes. Your private tags are managed separately (Manage → My Private Tags).
        </p>
        <app-tag-manager [spaceId]="space.id" />
      </div>
    </section>

    <!-- Members -->
    <section class="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-surface-700 dark:bg-surface-900" aria-labelledby="space-admin-heading-members">
      <ng-container *ngTemplateOutlet="sectionHeader; context: { $implicit: 'members', title: 'Members' }"></ng-container>
      <div id="space-admin-section-members" [hidden]="isCollapsed('members')">
        <p class="mt-0 px-4 text-sm text-gray-500 md:px-6">
          Every active NotesApp user is listed. Users with no role have no access to this Space.
        </p>

        <table class="w-full text-sm mt-4">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-200">
              <th class="px-4 py-3 text-left font-semibold text-gray-700">User</th>
              <th class="px-4 py-3 text-left font-semibold text-gray-700 w-48">Role</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of members; trackBy: trackByUserId"
                class="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
              <td class="px-4 py-3 text-gray-800 font-medium">{{ row.username }}</td>
              <td class="px-4 py-3">
                <!-- appendTo body: the role list opens over the page instead of being cut off by the card. -->
                <p-select
                  [options]="roleOptions"
                  optionLabel="label"
                  optionValue="value"
                  [ngModel]="row.role"
                  (onChange)="onRoleSelected(row, $event.value)"
                  [disabled]="row.saving"
                  appendTo="body"
                  [attr.data-testid]="'space-admin-role-' + row.userId"
                  styleClass="w-full" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

  </ng-container>

  <!-- Confirm dialog -->
  <p-confirmDialog appendTo="body"></p-confirmDialog>

</div>
